import type { FilterType } from "@amzn/ads-discovery-theme-service-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import { sortBy } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";

import { defaultContentSubTypeForInsightInUniversalView } from "../../config/filters.config";
import {
    ALL_FAMILIES,
    allPackagedInsightScope,
    CUSTOM_INSIGHTS,
    IHubContentTypeToLabelMap,
    MARKETING_NARRATIVES,
    PACKAGED_INSIGHTS,
    PRE_PACKAGED_INSIGHTS,
    STANDALONE_INSIGHTS,
} from "../../constants/filters";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { discoveryFilterStore } from "../../store/filters";
import type {
    ContentSubTypeFilterGroupItem,
    ContentSubTypeFilterLeafItem,
} from "../../types/filters/common";
import { IHubContentType } from "../../types/ihubContentType";
import { IHubContentProvider } from "../../types/providers";
import { isAccountAgnosticId } from "../../utils/permission.utils";

import {
    addAllTypeOption,
    addUatToInsightFilters,
    useFilterTypesAction,
} from "./useFilterTypesAction";

export function useFilterTypesLoader(): [
    AsyncStatus,
    ContentSubTypeFilterGroupItem[],
    (currentContentType?: IHubContentType, shouldReset?: boolean) => void,
] {
    const [contentType, getContentSubType, setTypes, setScopeToLabel] =
        useStore(discoveryFilterStore, (state) => [
            state.contentType,
            state.getContentSubType,
            state.setTypes,
            state.setScopeToLabel,
        ]);

    const [
        uatStandalone,
        uatPeerBenchmarking,
        uatPackagedInsights,
        useUnifiedSearch,
    ] = useStore(FeatureFlagsStore, (state) => [
        state.uatStandalone,
        state.uatPeerBenchmarking,
        state.uatPackagedInsights,
        state.unifiedSearch,
    ]);

    const [filterTypesState, filterTypesAction] = useFilterTypesAction();
    const [typeFilterData, setTypeFilterData] = useState<FilterType[]>([]);
    const marketingContentTypeFilters = useMemo(
        () =>
            typeFilterData.find(
                (t) =>
                    t.contentType ===
                    IHubContentTypeToLabelMap[
                        IHubContentType.MARKETING_CONTENT
                    ],
            ),
        [typeFilterData],
    );
    const insightTypeFilters = useMemo(
        () =>
            typeFilterData.find(
                (t) =>
                    t.contentType ===
                    IHubContentTypeToLabelMap[IHubContentType.INSIGHTS],
            ),
        [typeFilterData],
    );

    useEffect(() => {
        const fetchFilterTypes = async () => {
            let filterTypeResponse = await filterTypesAction.execute();

            if (uatStandalone || uatPeerBenchmarking) {
                filterTypeResponse = addUatToInsightFilters(filterTypeResponse);
            }

            filterTypeResponse = addAllTypeOption(filterTypeResponse);

            setTypeFilterData(filterTypeResponse);
        };
        void fetchFilterTypes();
    }, []);

    const getTypeFiltersData = useCallback(
        (currentContentType) => {
            if (currentContentType === IHubContentType.INSIGHTS) {
                return insightTypeFilters;
            }
            if (currentContentType === IHubContentType.MARKETING_CONTENT) {
                return marketingContentTypeFilters;
            }
            return undefined;
        },
        [marketingContentTypeFilters, insightTypeFilters],
    );

    const typeFilterGroups = useMemo(() => {
        const providers = getTypeFiltersData(contentType)?.providers;
        const groupsMap: { [key: string]: ContentSubTypeFilterGroupItem } = {};
        providers?.forEach((p) => {
            const groupLabel = p.provider?.label;
            const groupValue = p.provider?.value;
            if (groupLabel && groupValue) {
                const leafItems: ContentSubTypeFilterLeafItem[] = [];
                p.scopes?.forEach((s) => {
                    if (s.value !== undefined && p.provider && s) {
                        leafItems.push({
                            ...s,
                            fetchInfo: {
                                scope: s,
                                provider: p.provider,
                            },
                        });
                    }
                });

                if (
                    groupLabel === PRE_PACKAGED_INSIGHTS ||
                    groupLabel === CUSTOM_INSIGHTS
                ) {
                    if (!groupsMap[PACKAGED_INSIGHTS]) {
                        groupsMap[PACKAGED_INSIGHTS] = {
                            label: PACKAGED_INSIGHTS,
                            items: [],
                        };

                        if (!uatPackagedInsights || useUnifiedSearch) {
                            groupsMap[PACKAGED_INSIGHTS].items.push({
                                fetchInfo: {
                                    scope: allPackagedInsightScope,
                                    provider: {
                                        label: IHubContentProvider.UNIFIED,
                                        value: IHubContentProvider.UNIFIED,
                                    },
                                },
                            });
                        }
                    }
                    groupsMap[PACKAGED_INSIGHTS].items = [
                        ...groupsMap[PACKAGED_INSIGHTS].items,
                        ...leafItems,
                    ];
                } else {
                    if (!groupsMap[groupLabel]) {
                        groupsMap[groupLabel] = {
                            label: groupLabel,
                            items: leafItems,
                        };
                    }
                }
            }

            // Override specific providers and force them to use unified search
            // TODO: Once unified search is launched, we should fix this at the API level
            if (!uatPackagedInsights) {
                groupsMap[PACKAGED_INSIGHTS]?.items.forEach((item) => {
                    item.fetchInfo.provider = {
                        label: IHubContentProvider.UNIFIED,
                        value: IHubContentProvider.UNIFIED,
                    };
                });
            }

            if (useUnifiedSearch && !uatPackagedInsights) {
                groupsMap[PACKAGED_INSIGHTS]?.items.forEach((item) => {
                    item.fetchInfo.provider = {
                        label: IHubContentProvider.UNIFIED,
                        value: IHubContentProvider.UNIFIED,
                    };
                });

                groupsMap[STANDALONE_INSIGHTS]?.items.forEach((item) => {
                    item.fetchInfo.provider = {
                        label: IHubContentProvider.UNIFIED,
                        value: IHubContentProvider.UNIFIED,
                    };
                });

                groupsMap[MARKETING_NARRATIVES]?.items.forEach((item) => {
                    item.fetchInfo.provider = {
                        label: IHubContentProvider.UNIFIED,
                        value: IHubContentProvider.UNIFIED,
                    };
                });
            }
        });

        return sortAndArrangeGroups(groupsMap);
    }, [contentType, getTypeFiltersData]);

    const setDefaultTypeFilter = useCallback(
        (currentContentType = contentType, shouldReset = false) => {
            const currentTypeFiltersData =
                getTypeFiltersData(currentContentType);
            if (!currentTypeFiltersData) return;

            const currentContentSubType = getContentSubType(currentContentType);
            if (
                isAccountAgnosticId() &&
                currentContentType === IHubContentType.INSIGHTS
            ) {
                setTypes(
                    currentContentType,
                    defaultContentSubTypeForInsightInUniversalView,
                );
                return;
            }
            const defaultProvider = currentTypeFiltersData
                ?.defaultContentSubType?.provider?.value as IHubContentProvider;
            const defaultScope =
                currentTypeFiltersData?.defaultContentSubType?.scope?.value;

            const isInsights = currentContentType === IHubContentType.INSIGHTS;

            let provider = shouldReset
                ? defaultProvider
                : (currentContentSubType.provider ?? defaultProvider);

            let scope;
            if (shouldReset) {
                if (isInsights) {
                    if (
                        currentContentSubType.provider ===
                        IHubContentProvider.UNIFIED
                    ) {
                        scope = allPackagedInsightScope.value;
                        provider = IHubContentProvider.UNIFIED;
                    } else {
                        scope = ALL_FAMILIES;
                    }
                } else {
                    scope = defaultScope;
                }
            } else {
                if (isInsights) {
                    if (currentContentSubType.scope === defaultScope) {
                        scope = ALL_FAMILIES;
                    } else {
                        scope = currentContentSubType.scope ?? ALL_FAMILIES;
                    }
                } else {
                    scope = currentContentSubType.scope ?? defaultScope;
                }
            }

            setTypes(currentContentType, { provider, scope });
        },
        [contentType, getTypeFiltersData],
    );

    useEffect(() => {
        if (typeFilterData && typeFilterData.length > 0) {
            const map = {};
            typeFilterData.forEach((groupItem) => {
                groupItem.providers?.forEach((providerItem) => {
                    providerItem.scopes?.forEach((scopeItem) => {
                        if (scopeItem.value) {
                            map[scopeItem.value] = scopeItem.label;
                        }
                    });
                });
            });

            if (!uatPackagedInsights) {
                map[allPackagedInsightScope.value!] =
                    allPackagedInsightScope.label!;
            }

            setScopeToLabel(map);
        }
    }, [typeFilterData]);

    return [filterTypesState.status, typeFilterGroups, setDefaultTypeFilter];
}

export function sortAndArrangeGroups(groupsMap: {
    [key: string]: ContentSubTypeFilterGroupItem;
}): ContentSubTypeFilterGroupItem[] {
    return Object.values(groupsMap).map((group) => {
        if (group.label === "Standalone insights") {
            const allItem = group.items.find(
                (item) => item.value === ALL_FAMILIES,
            );
            if (allItem) {
                const filteredItems = group.items.filter(
                    (item) => item !== allItem,
                );
                const sortedItems = sortBy(
                    filteredItems,
                    (item) => item.label ?? "",
                );
                return {
                    ...group,
                    items: [allItem, ...sortedItems],
                };
            }
        }

        return {
            ...group,
            items: sortBy(group.items, (item) => item.label ?? ""),
        };
    });
}
